import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@crayon/design-system-react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  BillingMappingMigrationModel,
  BillingMotion,
  ProgramType,
  Source,
  Target,
} from "api/client.generated";
import { ValidationException } from "api/client.generated.extensions";
import FormAutocomplete, { FormAutocompleteOption } from "components/primitives/FormAutocomplete";
import FormCheckbox from "components/primitives/FormCheckbox";
import FormErrorMessage from "components/primitives/FormErrorMessage";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableHeaderCell from "components/primitives/TableHeaderCell";
import useSourcePartnerBillingMappings from "hooks/api/PartnerMigration/useSourcePartnerBillingMappings";
import useApi from "hooks/api/useApi";
import useSourceCustomers from "hooks/api/useSourceCustomers";
import { ChecklistIcon } from "images/MuiIcons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import DefaultPaginationOptions from "types/defaultPaginationOptions";
import NotificationMessage from "types/notification-message";
import TableColumnInfo from "types/table-column-info";
import * as yup from "yup";
import { useAuthorizedTargetsContext } from "../AuthorizedTargetsContext";
import MigratePartnerStepProps from "../MigratePartnerStepProps";
import MigrationFormActionButtons from "../MigrationFormActionButtons";
import WarningIconWithTooltip, { TooltipMessage } from "../WarningIconWithTooltip";

const BillingMappingsStep = ({ onStepComplete }: MigratePartnerStepProps) => {
  const { sourcePartnerMappings, isSourcePartnerMappingsFetching } =
    useSourcePartnerBillingMappings();
  const { partnersMigrationClient } = useApi();
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [page, setPage] = useState(0);
  const rowsPerPageOptions = useMemo<number[]>(() => DefaultPaginationOptions, []);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const { authorizedTargets } = useAuthorizedTargetsContext();

  const onPageChange = (_event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { customers, isCustomersFetching } = useSourceCustomers(
    Source.CloudIq,
    ProgramType.MicrosoftCsp,
  );

  const formSchema = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        checked: yup.boolean(),
        id: yup.string().required(),
        prismCustomerName: yup.string(),
        prismTenantName: yup.string(),
        ciqCustomer: yup
          .object()
          .shape({
            id: yup.string().required(), // ciqCustomerId
            label: yup.string().required(), // ciqCustomerName
          })
          .nullable()
          .when("checked", {
            is: (checked: boolean) => checked,
            then: (schema) => schema.required("Required"),
          }),
        target: yup.string().oneOf(Object.values(Target)),
        targetCompany: yup.string(),
        targetContract: yup.string(),
        syncEnabled: yup.boolean(),
        billingMotion: yup.string().oneOf(Object.values(BillingMotion)),
        isTargetAuthorized: yup.boolean(),
        IsTargetCustomerAgreementInUse: yup.boolean(),
      }),
    ),
  });

  type SchemaType = yup.InferType<typeof formSchema>;

  const { control, handleSubmit, clearErrors, getFieldState } = useForm<SchemaType>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
    keyName: "fieldId",
  });
  const fieldsWatch = useWatch({ control, name: "items" });

  useEffect(() => {
    if (isSourcePartnerMappingsFetching || !sourcePartnerMappings) return;

    if (isCustomersFetching) return;

    // if rerender, clean up fields to avoid duplication
    if (fields.length > 0) remove([...Array(fields.length).keys()]);

    sourcePartnerMappings.forEach((x) => {
      let ciqCustomer: FormAutocompleteOption | null = null;
      if (x.isTargetCustomerAgreementInUse) {
        const customer = customers?.find((c) => c.id === x.assignedCiqCustomerId);
        if (customer) {
          ciqCustomer = { id: customer.id, label: customer.name ?? "" };
        }
      }

      append({
        checked: false,
        id: x.id,
        ciqCustomer,
        prismCustomerName: x.sourceCustomer.name,
        prismTenantName: x.sourceCustomer.agreement?.name ?? undefined,
        target: x.target,
        targetCompany: x.targetCustomer.name,
        targetContract: x.targetCustomer.agreement?.name ?? undefined,
        syncEnabled: x.isSyncing,
        billingMotion: x.billingMotion,
        isTargetAuthorized: authorizedTargets.some((t) => t === x.target),
        IsTargetCustomerAgreementInUse: x.isTargetCustomerAgreementInUse,
      });
    });
  }, [
    sourcePartnerMappings,
    isSourcePartnerMappingsFetching,
    append,
    remove,
    fields.length,
    authorizedTargets,
    customers,
    isCustomersFetching,
  ]);

  // to clear validation errors when on item unselect action
  useEffect(() => {
    fieldsWatch
      ?.map((x, i) => ({ checked: x.checked, index: i }))
      .filter((x) => !x.checked)
      .forEach((x) => {
        clearErrors(`items.${x.index}.ciqCustomer`);
      });
  }, [fieldsWatch, clearErrors]);

  const columnOptions = useMemo<TableColumnInfo[]>(
    () => [
      { label: "" },
      { label: "Prism Customer" },
      { label: "Prism Tenant" },
      { label: "Cloud-iQ Customer", width: "20%" },
      { label: "Billing Motion" },
      { label: "PSA" },
      { label: "PSA Company/Customer" },
      { label: "PSA Contract/Agreement" },
      { label: "Sync Enabled" },
    ],
    [],
  );

  const customerSelectOptions = useCallback(
    (index: number): FormAutocompleteOption[] => {
      const fieldWithSelectedCiqCustomer = fieldsWatch?.filter((x) => !!x.ciqCustomer);

      // if there is already selected CIQ customer for the same PRISM
      // customer, then limit selection to the same CIQ customer
      const samePrismCustomerField = fieldWithSelectedCiqCustomer?.find(
        (x) => x.prismCustomerName === fieldsWatch?.at(index)?.prismCustomerName,
      );

      if (samePrismCustomerField) {
        return [{ ...samePrismCustomerField.ciqCustomer } as FormAutocompleteOption];
      }

      // Otherwise, exclude selected CIQ customers from options
      return (
        customers
          ?.filter((x) => !fieldWithSelectedCiqCustomer?.some((y) => y.ciqCustomer?.id === x.id))
          .map((x) => ({ id: x.id, label: x.name }) as FormAutocompleteOption) ?? []
      );
    },
    [customers, fieldsWatch],
  );

  const onSave = async (formData: SchemaType) => {
    try {
      setIsSaving(true);

      const migrations: BillingMappingMigrationModel[] =
        formData.items
          ?.filter((x) => x.checked)
          .map((x) => ({
            billingMappingId: x.id,
            cloudIqCustomerId: x.ciqCustomer!.id,
          })) ?? [];

      if (migrations.length === 0) return;

      await partnersMigrationClient.migrateBillingMappings(migrations);

      onStepComplete();
    } catch (e: unknown) {
      const validationError = ValidationException.parse(e);
      if (validationError) {
        setFormErrorMsg(validationError.getFirstErrorMessage());
      } else {
        setFormErrorMsg(NotificationMessage.UNKNOWN_ERROR);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // this is to set page to the 1st invalid form field
  const onInvalid = () => {
    for (let i = 0; i < fields.length; i += 1) {
      const { error } = getFieldState(`items.${i}.ciqCustomer`);
      if (error) {
        setPage(Math.floor(i / rowsPerPage));
        break;
      }
    }
  };

  const DataTableHead = (
    <TableHead>
      <TableRow>
        <TableCell size="small" sx={{ width: "45px" }}>
          <Box display="flex">
            <ChecklistIcon />
          </Box>
        </TableCell>
        {columnOptions.slice(1).map((x, i) => (
          <TableHeaderCell key={x.label + String(i)} columnName={x.label} width={x.width} />
        ))}
      </TableRow>
    </TableHead>
  );

  const DataTableBody = (
    <TableBody>
      {fields.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((x, i) => {
        const fieldIndex = i + page * rowsPerPage;
        return (
          <TableRow key={x.fieldId}>
            <TableCell>
              <FormCheckbox
                control={control}
                bindSchemaFieldName={`items.${fieldIndex}.checked`}
                label=""
                disabled={isSaving || !x.isTargetAuthorized || x.IsTargetCustomerAgreementInUse}
              />
            </TableCell>
            <TableBodyCell label={x.prismCustomerName} />
            <TableBodyCell label={x.prismTenantName} />
            <TableCell sx={{ minWidth: "250px" }}>
              <Box display="flex">
                <FormAutocomplete
                  control={control}
                  bindSchemaFieldName={`items.${fieldIndex}.ciqCustomer`}
                  label="Select customer"
                  disabled={!fieldsWatch?.at(fieldIndex)?.checked || isSaving}
                  options={customerSelectOptions(fieldIndex)}
                  isLoading={isCustomersFetching}
                  sxRoot={{ flex: 1 }}
                  clearable
                  testId="ciq-customer-autocomplete"
                />
              </Box>
            </TableCell>
            <TableBodyCell label={x.billingMotion} />
            <TableBodyCell
              label={x.target}
              {...(!x.isTargetAuthorized && {
                icon: <WarningIconWithTooltip message={TooltipMessage.PsaNotAuthorized} />,
              })}
            />
            <TableBodyCell label={x.targetCompany} />
            <TableBodyCell
              label={x.targetContract}
              {...(x.IsTargetCustomerAgreementInUse && {
                icon: <WarningIconWithTooltip message={TooltipMessage.ContractIsInUse} />,
              })}
            />
            <TableBodyCell label={x.syncEnabled ? "Yes" : "No"} />
          </TableRow>
        );
      })}
    </TableBody>
  );

  return (
    <form onSubmit={handleSubmit(onSave, onInvalid)}>
      <Box display="flex" flexDirection="column" gap={1}>
        <TableContainer component={Paper}>
          <Table testId="migrate-mappings-table">
            {DataTableHead}
            {isSourcePartnerMappingsFetching ? (
              <TableBodySkeleton colsCount={columnOptions.length} rowsCount={3} />
            ) : (
              DataTableBody
            )}
          </Table>
          {!isSourcePartnerMappingsFetching && (
            <TablePagination
              component={Box}
              count={fields.length}
              page={page}
              onPageChange={onPageChange}
              rowsPerPageOptions={rowsPerPageOptions}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              sx={{ my: 1 }}
            />
          )}
        </TableContainer>
        <FormErrorMessage message={formErrorMsg} />
        <MigrationFormActionButtons
          isSaveDisable={!fieldsWatch?.some((x) => x.checked)}
          isSaveLoading={isSaving}
        />
      </Box>
    </form>
  );
};

export default BillingMappingsStep;
